import React from 'react';
import { Button } from '../../components/ui/button';
import { ShoppingBag, CreditCard, Banknote, CheckCircle, StickyNote, Clock, Plus, Printer, Receipt, Bike } from 'lucide-react';
import { printKitchenTicket, printClientReceipt } from '../../components/PrintTickets';

function getWaitMinutes(dateStr) {
  if (!dateStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
}

export const ServerDashboard = ({
  orders, myTables, setRecapTableId, setView, startNewOrder, startGrabOrder,
  startPlatformOrder, deliveryPlatforms, PLATFORM_META,
  openPaymentForOrder, formatPrice, statusColors, serverName,
}) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Tables strip */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          {myTables.map(tableId => {
            const tableOrders = orders.filter(o => o.table_id === tableId);
            const hasActive = tableOrders.length > 0;
            const tableTotal = tableOrders.reduce((s, o) => s + o.total - (o.discount_amount || 0), 0);
            const oldestOrder = tableOrders.length > 0
              ? tableOrders.reduce((old, o) => (!old || new Date(o.created_at) < new Date(old.created_at) ? o : old), null)
              : null;
            const waitMin = oldestOrder ? getWaitMinutes(oldestOrder.created_at) : 0;
            const hasReadyOrder = tableOrders.some(o => o.status === 'ready');
            return (
              <button key={tableId} onClick={() => {
                if (hasActive) { setRecapTableId(tableId); setView('tablerecap'); }
                else { startNewOrder(tableId); }
              }}
                className={`flex-shrink-0 rounded-xl px-4 py-2 text-center border-2 transition-all active:scale-95 min-w-[70px] ${
                  hasReadyOrder ? 'bg-[#2D6A4F]/10 border-[#2D6A4F] animate-pulse'
                    : hasActive ? 'bg-[#E16833]/10 border-[#E16833]'
                      : 'bg-white border-[#E5DCD3]'
                }`}
                data-testid={`table-quick-${tableId}`}>
                <p className="text-sm font-bold text-[#2D241D]">T{tableId}</p>
                {hasActive ? (
                  <>
                    <p className="text-[10px] font-bold text-[#E16833]">{formatPrice(tableTotal)}</p>
                    {waitMin > 0 && (
                      <p className={`text-[9px] font-mono font-bold mt-0.5 ${
                        waitMin > 20 ? 'text-red-500' : waitMin > 10 ? 'text-[#F59E0B]' : 'text-[#6E5E52]'
                      }`} data-testid={`table-wait-${tableId}`}>
                        {waitMin}min
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] text-[#6E5E52]">Libre</p>
                )}
              </button>
            );
          })}
          <button onClick={() => startNewOrder()} className="flex-shrink-0 rounded-xl px-4 py-2 text-center border-2 border-dashed border-[#D4A853] bg-[#D4A853]/5 active:scale-95 min-w-[70px]"
            data-testid="table-takeaway-btn">
            <p className="text-sm font-bold text-[#D4A853]">+</p>
            <p className="text-[10px] text-[#6E5E52]">Emporter</p>
          </button>
          {deliveryPlatforms.map(p => {
            const pid = typeof p === 'string' ? p : p.id;
            const meta = PLATFORM_META[pid] || { name: p.name || pid, color: '#888' };
            return (
              <button key={pid} onClick={() => startPlatformOrder(pid)}
                className="flex-shrink-0 rounded-xl px-4 py-2 text-center border-2 border-dashed active:scale-95 min-w-[70px]"
                style={{ borderColor: meta.color, backgroundColor: `${meta.color}08` }}
                data-testid={`table-platform-${pid}`}>
                <Bike className="w-5 h-5 mx-auto" style={{ color: meta.color }} />
                <p className="text-[10px] font-bold" style={{ color: meta.color }}>{meta.name}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders list */}
      <div className="px-3 pb-3">
        {orders.length === 0 && (
          <div className="text-center py-16 text-[#6E5E52]">
            <ShoppingBag className="w-14 h-14 mx-auto mb-3 opacity-20" />
            <p className="font-medium text-lg">Aucune commande en cours</p>
            <p className="text-sm mt-1">Appuyez sur <strong>+ Nouvelle commande</strong> pour commencer</p>
          </div>
        )}
        <div className="space-y-3">
          {orders.map(order => {
            const afterDiscount = order.total - (order.discount_amount || 0);
            const remaining = afterDiscount - (order.paid_amount || 0);
            const isPaid = remaining <= 0;
            const waitMin = getWaitMinutes(order.created_at);
            return (
              <div key={order.id} className={`bg-white rounded-2xl border overflow-hidden ${
                isPaid ? 'border-[#2D6A4F]/30 opacity-60'
                  : order.status === 'ready' ? 'border-[#2D6A4F] ring-2 ring-[#2D6A4F]/20'
                    : 'border-[#E5DCD3]'
              }`} data-testid={`server-order-${order.id}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#F3EDE5]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-[#2D241D]">
                      {order.is_takeaway ? 'A emporter' : `Table ${order.table_id}`}
                    </span>
                    {order.order_source && order.order_source !== 'dine_in' && order.order_source !== 'takeaway' && (() => {
                      const meta = PLATFORM_META[order.order_source] || { name: order.order_source.toUpperCase(), color: '#888' };
                      return (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                          data-testid={`platform-badge-${order.id}`}>
                          {meta.name.toUpperCase()}
                        </span>
                      );
                    })()}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[order.status] || 'bg-gray-100'}`}>
                      {order.status === 'ready' ? 'PRET A SERVIR' : order.status}
                    </span>
                    {order.payment_method && (
                      <span className="text-[10px] text-[#6E5E52] flex items-center gap-0.5">
                        {order.payment_method === 'cash' ? <Banknote className="w-3 h-3 text-[#2D6A4F]" /> : <CreditCard className="w-3 h-3 text-[#6366F1]" />}
                        {order.payment_method === 'card_terminal' ? 'CB' : order.payment_method}
                      </span>
                    )}
                    {/* Wait time badge */}
                    {!isPaid && waitMin > 0 && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${
                        waitMin > 20 ? 'bg-red-100 text-red-600'
                          : waitMin > 10 ? 'bg-[#F59E0B]/15 text-[#F59E0B]'
                            : 'bg-gray-100 text-[#6E5E52]'
                      }`} data-testid={`order-wait-${order.id}`}>
                        <Clock className="w-2.5 h-2.5 inline mr-0.5" />{waitMin}min
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#E16833]">{formatPrice(afterDiscount)}</p>
                    <p className="text-[10px] text-[#6E5E52]">
                      <Clock className="w-3 h-3 inline mr-0.5" />
                      {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {order.notes && (
                  <div className="px-4 py-2 bg-[#FFF3E0] flex items-start gap-2">
                    <StickyNote className="w-3.5 h-3.5 text-[#E16833] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-[#6E5E52] italic">{order.notes}</p>
                  </div>
                )}

                <div className="px-4 py-2 space-y-1">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className={`text-[#2D241D] ${item.is_ready ? 'line-through opacity-50' : ''}`}>
                        {item.quantity}x {item.name}
                        {item.is_ready && <CheckCircle className="w-3 h-3 inline ml-1 text-[#2D6A4F]" />}
                      </span>
                      <span className="text-[#6E5E52]">{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                  {order.discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-[#E63946] pt-1 border-t border-[#F3EDE5]">
                      <span>Remise</span><span>-{formatPrice(order.discount_amount)}</span>
                    </div>
                  )}
                </div>

                {!isPaid ? (
                  <div className="px-4 py-3 bg-[#F3EDE5] flex gap-2">
                    <Button onClick={() => openPaymentForOrder(order, 'checkout')}
                      className={`flex-1 text-white rounded-xl h-10 text-sm font-semibold ${
                        order.status === 'ready' ? 'bg-[#2D6A4F] hover:bg-[#1B4332]' : 'bg-[#0A422D] hover:bg-[#083323]'
                      }`}
                      data-testid={`server-pay-${order.id}`}>
                      <Banknote className="w-4 h-4 mr-1.5" /> Encaisser ({formatPrice(remaining)})
                    </Button>
                    <button onClick={() => printKitchenTicket({ tableId: order.table_id, items: order.items, notes: order.notes, orderId: order.id, createdAt: order.created_at, serverName })}
                      className="h-10 w-10 rounded-xl bg-[#D4A853]/10 text-[#D4A853] flex items-center justify-center hover:bg-[#D4A853] hover:text-white transition-colors"
                      data-testid={`print-kitchen-dash-${order.id}`}>
                      <Printer className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="px-4 py-2 bg-[#E8F5E9] flex items-center justify-between text-sm text-[#2D6A4F] font-semibold">
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Paye</span>
                    <button onClick={() => printClientReceipt({ tableId: order.table_id, items: order.items, total: order.total, discountAmount: order.discount_amount, paymentMethod: order.payment_method, orderId: order.id, createdAt: order.created_at, serverName, isTakeaway: order.is_takeaway })}
                      className="flex items-center gap-1 text-xs text-[#6E5E52] hover:text-[#0A422D]"
                      data-testid={`print-receipt-dash-${order.id}`}>
                      <Receipt className="w-3.5 h-3.5" /> Ticket
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
